import React, { useEffect, useMemo, useState } from "react";
import { formatMoneyCode } from "../lib/formatMoney"; // relative: must REALLY resolve under Jest (no "@/" alias there)
import { pluralKey } from "../lib/pluralKey";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Loader2, GraduationCap, ArrowLeft, Search, FileQuestion, Filter, Clock, Sparkles, ShoppingBag, UserCircle2, ExternalLink } from "lucide-react";
// Relative, not "@/...": real modules under Jest, no alias there.
import ConsultRateLabel from "./ConsultRateLabel";
import { consultOf, isPerMinute, isUnbookablePerMinute } from "./consultPricing";
// `withRef` + `SiteLink` now live beside the profile page that shares them.
import { SiteLink } from "./marketplaceLinks";
import RatingBadge from "../reviews/components/RatingBadge";
import { practitionerProfilePath, PROMOTION_QUERY_PARAM } from "../reviews/contract";
import { REVIEWS } from "../constants/testIds/reviews";
// crm-module BEGIN C3.02/C3.04 (lane F4)
import PromotedBadge from "./marketplace/PromotedBadge";
import FeaturedStrip from "./marketplace/FeaturedStrip";
// crm-module END

const TABS_DEF = [
  { id: "practitioners", icon: UserCircle2 },
  { id: "courses", icon: GraduationCap },
  { id: "products", icon: ShoppingBag },
];

const PRICE_FILTERS = ["all", "free", "paid"];

// Formateur de prix partagé (cours + services praticiens) : mêmes unités partout
// (price_cents), donc UNE seule implémentation plutôt que la reproduire par carte.
const formatCardPrice = (cents, currency) => formatMoneyCode(cents, currency);

/**
 * MarketplaceLearn — Catalogue cross-tenant unifié : praticiens + cours + produits
 * avec onglets et filtres. Public sur /marketplace et /learn.
 */
export default function MarketplaceLearn({ initialTab = "practitioners" }) {
  const t = useT("marketplace");
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/learn")) {
      return "courses";
    }
    return initialTab;
  });
  const [search, setSearch] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [practitionerFilter, setPractitionerFilter] = useState("all");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");

  const [courses, setCourses] = useState(null);
  const [products, setProducts] = useState(null);
  const [practitioners, setPractitioners] = useState(null);
  const [specialties, setSpecialties] = useState([]);

  useEffect(() => {
    api.get("/marketplace/courses").then(({ data }) => setCourses(data || [])).catch(() => setCourses([]));
    api.get("/marketplace/products").then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
    api.get("/marketplace/specialties").then(({ data }) => setSpecialties(data || [])).catch(() => setSpecialties([]));
  }, []);

  // crm-module BEGIN C3.02/C3.04 (lane F4) — promoted profiles sort first only on a
  // SERVER-SCOPED read (`telehealth_marketplace_routes.py`: "an unscoped `?specialty=`
  // browse has no specialty for 'within' to mean anything, so no profile is boosted on
  // it"). The specialty chips used to only filter the one unscoped fetch client-side —
  // which would make `promoted:true` (and the badge it drives) permanently unreachable.
  // Re-fetching on every `specialtyFilter` change (including back to "all") keeps the
  // list server-sorted for whichever scope is active; the existing client-side filter
  // in `filtered` below becomes a no-op once the fetch is already scoped, not a second,
  // conflicting order (ORDER IS THE SERVER'S — see that memo's own comment).
  useEffect(() => {
    let alive = true;
    const params = specialtyFilter !== "all" ? { specialty: specialtyFilter } : {};
    api.get("/marketplace/practitioners", { params })
      .then(({ data }) => { if (alive) setPractitioners(data || []); })
      .catch(() => { if (alive) setPractitioners([]); });
    return () => { alive = false; };
  }, [specialtyFilter]);
  // crm-module END

  const allPractitioners = useMemo(() => {
    if (!practitioners) return [];
    return practitioners.filter((p) => p.courses_count > 0 || p.products_count > 0);
  }, [practitioners]);

  // ORDER IS THE SERVER'S, AND THIS MEMO MAY ONLY EVER REMOVE ROWS FROM IT. The
  // practitioners array arrives sorted by `rank_score desc, review_count desc, name asc`
  // — a Bayesian mean computed from the platform-wide prior, which the browser does not
  // hold and cannot recompute. `filter` preserves order; a `sort` here (by name, by rating,
  // by anything) would silently replace the ranking with a second, wrong one and the badge
  // a card shows would stop explaining why it sits where it sits.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (tab === "practitioners") {
      let list = practitioners || [];
      if (specialtyFilter !== "all") {
        list = list.filter((p) => (p.specialties || []).map((s) => s.toLowerCase()).includes(specialtyFilter));
      }
      if (!q) return list;
      return list.filter((p) => `${p.name || ""} ${p.tagline || ""} ${(p.specialties || []).join(" ")}`.toLowerCase().includes(q));
    }
    const list = tab === "courses" ? (courses || []) : (products || []);
    return list.filter((it) => {
      // `priceFilter` is only reachable from the courses/products branch (the
      // practitioners tab returned above and has no price filter), so it cannot classify
      // a per-minute service as free — a per-minute row's `price_cents` is 0 by design.
      // Any future practitioner price filter MUST branch on `pricing_mode` instead.
      const price = it.price_cents || 0;
      if (priceFilter === "free" && price > 0) return false;
      if (priceFilter === "paid" && price === 0) return false;
      if (practitionerFilter !== "all" && it._tenant_slug !== practitionerFilter) return false;
      if (!q) return true;
      const blob = `${it.title || it.name || ""} ${it.description || ""} ${it._tenant_name || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [tab, courses, products, practitioners, search, priceFilter, practitionerFilter, specialtyFilter]);

  const isLoading = (tab === "courses" && courses === null) || (tab === "products" && products === null) || (tab === "practitioners" && practitioners === null);

  return (
    <div className="min-h-screen bg-sol-midnight text-white" data-testid="marketplace-learn">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Solunary
          </Link>
          <LanguageSwitcher variant="dark" />
        </div>

        <div className="mb-12 flex items-start gap-4">
          <Sparkles className="w-12 h-12 text-sol-gold mt-2" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-sol-gold/80 mb-2 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("label")}
            </div>
            <h1 className="font-display font-semibold text-5xl mb-2 bg-gradient-to-r from-sol-gold via-rose-200 to-sol-sage bg-clip-text text-transparent">
              {t("title")}
            </h1>
            <p className="text-white/60 max-w-2xl">{t("subtitle")}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto">
          {TABS_DEF.map((tdef) => {
            const Icon = tdef.icon;
            const count = tdef.id === "courses" ? (courses?.length ?? 0) : tdef.id === "products" ? (products?.length ?? 0) : (practitioners?.length ?? 0);
            return (
              <button
                key={tdef.id}
                onClick={() => { setTab(tdef.id); setSearch(""); setPriceFilter("all"); setPractitionerFilter("all"); }}
                data-testid={`mp-tab-${tdef.id}`}
                className={`relative px-5 py-3 text-sm font-medium inline-flex items-center gap-2 transition ${
                  tab === tdef.id ? "text-sol-gold" : "text-white/50 hover:text-white/80"
                }`}
              >
                <Icon className="w-4 h-4" /> {t(`tab.${tdef.id}`)} <span className="text-xs opacity-50">({count})</span>
                {tab === tdef.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sol-gold" />}
              </button>
            );
          })}
        </div>

        {/* Search + filters */}
        <div className="flex flex-col lg:flex-row gap-3 mb-8">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "practitioners" ? t("search.practitioners") : t("search.products")}
              data-testid="marketplace-search"
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-10 pr-4 h-12 text-sm placeholder:text-white/40 focus:outline-none focus:border-sol-gold/50 transition"
            />
          </div>
          {tab !== "practitioners" && (
            <>
              <div className="flex gap-2 items-center">
                <Filter className="w-4 h-4 text-white/40" />
                {PRICE_FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setPriceFilter(f)}
                    data-testid={`marketplace-filter-${f}`}
                    className={`px-4 h-9 rounded-full text-xs transition ${
                      priceFilter === f ? "bg-sol-gold text-black font-medium" : "bg-white/[0.04] text-white/60 hover:bg-white/[0.08]"
                    }`}
                  >
                    {t(`filter.${f}`)}
                  </button>
                ))}
              </div>
              <select
                value={practitionerFilter}
                onChange={(e) => setPractitionerFilter(e.target.value)}
                data-testid="marketplace-practitioner-filter"
                className="bg-white/[0.04] border border-white/10 rounded-full px-4 h-9 text-xs text-white/70 focus:outline-none focus:border-sol-gold/50"
              >
                <option value="all">{t("filter.allPractitioners")}</option>
                {allPractitioners.map((p) => (
                  <option key={p.tenant_id} value={p.slug}>{p.name}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Specialty chips (practitioners tab only) */}
        {tab === "practitioners" && specialties.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="specialty-chips">
            <button
              onClick={() => setSpecialtyFilter("all")}
              className={`px-3 py-1 rounded-full text-xs transition ${
                specialtyFilter === "all" ? "bg-sol-gold text-black font-medium" : "bg-white/[0.04] text-white/60 hover:bg-white/[0.08]"
              }`}
            >
              {t("filter.allSpecialties")}
            </button>
            {specialties.map((s) => (
              <button
                key={s.slug}
                onClick={() => setSpecialtyFilter(s.slug)}
                data-testid={`chip-${s.slug}`}
                className={`px-3 py-1 rounded-full text-xs transition capitalize ${
                  specialtyFilter === s.slug ? "bg-sol-gold text-black font-medium" : "bg-white/[0.04] text-white/60 hover:bg-white/[0.08]"
                }`}
              >
                {s.slug} <span className="opacity-60">({s.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* crm-module BEGIN C3.02/C3.04 — Promote's "featured strip" placement (lane F4).
            Independent read, own loading state; renders nothing while empty/loading, so
            it never blocks or reorders the regular grid below it. */}
        {tab === "practitioners" && <FeaturedStrip category={specialtyFilter !== "all" ? specialtyFilter : undefined} />}
        {/* crm-module END */}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-sol-gold" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-white/40">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
            {t("empty")}
          </div>
        ) : tab === "practitioners" ? (
          <PractitionersGrid practitioners={filtered} t={t} />
        ) : tab === "products" ? (
          <ProductsGrid products={filtered} t={t} />
        ) : (
          <CoursesGrid courses={filtered} t={t} />
        )}
      </div>
    </div>
  );
}

// ===== Sub-components =====
function CoursesGrid({ courses, t }) {
  const tc = useT("common");
  const { lang } = useLanguage();
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="marketplace-grid">
      {courses.map((c) => {
        const free = (c.price_cents || 0) === 0;
        // The server's own sellability statement (course_rules): a course with no lesson
        // cannot be bought or enrolled in, so its card advertises no price.
        const noLessons = c.unpayable_reason === "no_lessons";
        const lessonsCount = (c.lessons || []).length;
        const quizCount = (c.lessons || []).filter((l) => l.lesson_type === "quiz").length;
        const coursePath = c.promotion_id
          ? `/learn/${c.id}?${PROMOTION_QUERY_PARAM}=${encodeURIComponent(c.promotion_id)}`
          : `/learn/${c.id}`;
        return (
          <SiteLink
            key={c.id}
            siteUrl={c._site_url}
            tenantSlug={c._tenant_slug}
            path={coursePath}
            onClick={() => {
              if (c.promotion_id) {
                api.post(`/marketplace/promotions/${c.promotion_id}/click`, {}).catch(() => {});
              }
            }}
            data-testid={`marketplace-course-${c.id}`}
            className="group rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-sol-gold/40 hover:bg-white/[0.05] transition-all flex flex-col"
          >
            {c.cover_url ? (
              <img src={c.cover_url} alt="" className="aspect-video object-cover w-full" />
            ) : (
              <div className="aspect-video bg-gradient-to-br from-sol-gold/10 via-white/[0.03] to-sol-sage/10 flex items-center justify-center">
                <GraduationCap className="w-12 h-12 text-sol-gold/40" />
              </div>
            )}
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-2">
                {c._tenant_name && (
                  <div className="text-[10px] uppercase tracking-wider text-sol-gold/80 flex items-center gap-2">
                    {c._tenant_logo && <img src={c._tenant_logo} alt="" className="w-4 h-4 rounded-full object-cover" />}
                    {t("card.by")} {c._tenant_name}
                  </div>
                )}
                <PromotedBadge promoted={c.promoted} testId={`marketplace-course-promoted-${c.id}`} />
              </div>
              <h3 className="font-display text-xl text-white mb-2 line-clamp-2 group-hover:text-sol-gold transition">{c.title}</h3>
              <p className="text-sm text-white/50 line-clamp-3 flex-1 mb-4">{c.description || "—"}</p>
              <div className="flex items-center justify-between text-xs text-white/40 pt-3 border-t border-white/5">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1" data-testid={`marketplace-course-lessons-${c.id}`}><Clock className="w-3 h-3" /> {tc(pluralKey(lang, lessonsCount, "count.lessons"), { n: lessonsCount })}</span>
                  {quizCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-amber-300/80">
                      <FileQuestion className="w-3 h-3" /> {quizCount} {t("card.quiz")}
                    </span>
                  )}
                </div>
                {noLessons ? (
                  <span className="text-white/50" data-testid={`marketplace-course-unavailable-${c.id}`}>
                    {t("card.notAvailableYet")}
                  </span>
                ) : (
                  <span className={`font-medium text-base ${free ? "text-emerald-300" : "text-sol-gold"}`}>
                    {free ? t("card.free") : formatCardPrice(c.price_cents, c.currency)}
                  </span>
                )}
              </div>
            </div>
          </SiteLink>
        );
      })}
    </div>
  );
}

function ProductsGrid({ products, t }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="marketplace-products-grid">
      {products.map((p) => {
        const productPath = p.promotion_id
          ? `/boutique?${PROMOTION_QUERY_PARAM}=${encodeURIComponent(p.promotion_id)}`
          : "/boutique";
        return (
          <SiteLink
            key={p.id}
            siteUrl={p._site_url}
            tenantSlug={p._tenant_slug}
            path={productPath}
            onClick={() => {
              if (p.promotion_id) {
                api.post(`/marketplace/promotions/${p.promotion_id}/click`, {}).catch(() => {});
              }
            }}
            data-testid={`marketplace-product-${p.id}`}
            className="group rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-sol-gold/40 hover:bg-white/[0.05] transition-all flex flex-col"
          >
            {p.image_url ? (
              <img src={p.image_url} alt="" className="aspect-square object-cover w-full" />
            ) : (
              <div className="aspect-square bg-gradient-to-br from-sol-gold/10 to-sol-sage/10 flex items-center justify-center">
                <ShoppingBag className="w-10 h-10 text-sol-gold/40" />
              </div>
            )}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                {p._tenant_name && (
                  <div className="text-[9px] uppercase tracking-wider text-sol-gold/80 flex items-center gap-1.5">
                    {p._tenant_logo && <img src={p._tenant_logo} alt="" className="w-3 h-3 rounded-full object-cover" />}
                    {p._tenant_name}
                  </div>
                )}
                <PromotedBadge promoted={p.promoted} testId={`marketplace-product-promoted-${p.id}`} />
              </div>
              <h3 className="font-display text-base text-white mb-1.5 line-clamp-2 group-hover:text-sol-gold transition">{p.name}</h3>
              <p className="text-xs text-white/50 line-clamp-2 flex-1 mb-2">{p.description || "—"}</p>
              <div className="text-sol-gold font-medium text-base">{p.price_cents > 0 ? formatCardPrice(p.price_cents, p.currency) : "—"}</div>
            </div>
          </SiteLink>
        );
      })}
    </div>
  );
}


function PractitionersGrid({ practitioners, t }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketplace-practitioners-grid">
      {practitioners.map((p) => {
        // A per-minute service with no active rate is NOT advertised: booking it would
        // 409 PRICING_NOT_SET, so the pill would be a dead end — the same hide
        // `Reserver.jsx` applies to the practitioner's own chooser. The "+N more" badge
        // is therefore counted against what is actually SHOWN, not against the raw
        // preview, or it would promise services the visitor cannot reach.
        const services = (p.services || []).filter((s) => !isUnbookablePerMinute(s));
        const moreServices = Math.max(0, (p.services_total || 0) - services.length);
        return (
          <div
            key={p.tenant_id}
            data-testid={`marketplace-practitioner-${p.tenant_id}`}
            className="group rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-sol-gold/40 hover:bg-white/[0.05] transition-all flex flex-col gap-4"
          >
            {/* THE HEADER IS NOW SAME-ORIGIN, and only the header. Clicking a practitioner's
                name used to leave the platform for their own site, which skipped the one page
                that states what the marketplace knows about them — every service with its
                description, the rating breakdown and the published reviews. "Book" below
                still crosses the origin (it is a purchase on the practice's site and carries
                the referral); the profile is a platform page and is a router <Link>. */}
            <Link
              // --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
              // A promoted card (`p.promotion_id` present, C3.02's `promoted_tenant_ids`
              // map) carries `?pr=` the SAME way `FeaturedStrip` does; a non-promoted
              // card's plain path is unchanged.
              to={p.promotion_id
                ? `${practitionerProfilePath(p.slug)}?${PROMOTION_QUERY_PARAM}=${encodeURIComponent(p.promotion_id)}`
                : practitionerProfilePath(p.slug)}
              onClick={() => {
                if (p.promotion_id) {
                  api.post(`/marketplace/promotions/${p.promotion_id}/click`, {}).catch(() => {});
                }
              }}
              // --- crm-module END ---
              data-testid={REVIEWS.cardProfileLink(p.tenant_id)}
              className="flex items-center gap-4"
            >
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="w-16 h-16 rounded-full object-cover flex-shrink-0 border border-white/10" />
              ) : (
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-display flex-shrink-0"
                  style={{ background: p.primary_color ? p.primary_color + "30" : "rgba(230,184,92,0.15)", color: p.primary_color || "#E6B85C" }}
                >
                  {(p.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg text-white group-hover:text-sol-gold transition truncate">{p.name}</h3>
                  {/* crm-module BEGIN C3.02/C3.04 — Promote's marketplace sort (lane F4).
                      The SERVER decides the order (`promoted:true` sorts first within a
                      specialty); this only names why a card sits where it does. */}
                  <PromotedBadge promoted={p.promoted} testId={`marketplace-practitioner-promoted-${p.tenant_id}`} />
                  {/* crm-module END */}
                </div>
                {p.tagline && <p className="text-xs text-white/50 line-clamp-2 mt-1">{p.tagline}</p>}
                {/* Renders NOTHING when the payload carries no `rating` key at all — an
                    older cached list must not grow an invented badge (see RatingBadge). */}
                <RatingBadge rating={p.rating} tenantId={p.tenant_id} t={t} variant="dark" />
                {(p.specialties || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] bg-sol-gold/15 text-sol-gold px-2 py-0.5 rounded-full capitalize">{s}</span>
                    ))}
                    {p.specialties.length > 3 && <span className="text-[10px] text-white/40">+{p.specialties.length - 3}</span>}
                  </div>
                )}
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="text-white/40">{p.courses_count} {t("card.courses")}</span>
                  <span className="text-white/40">·</span>
                  <span className="text-white/40">{p.products_count} {t("card.products")}</span>
                </div>
              </div>
            </Link>

            {services.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-3 border-t border-white/5" data-testid={`practitioner-services-${p.tenant_id}`}>
                {services.map((s) => (
                  <span key={s.id} className="text-[11px] bg-white/[0.05] text-white/70 px-2.5 py-1 rounded-full">
                    {s.name} ·{" "}
                    {/* A per-minute consultation states its RATE and its ceiling, never a
                        catalogue price: `price_cents` is 0 for one by design, and reading
                        that 0 as "free" advertised every paid consult on the platform's
                        own storefront as free (QA-02). Same component, same wire fields
                        as the practitioner's own /reserver. */}
                    {isPerMinute(s) ? (
                      <ConsultRateLabel
                        consult={consultOf(s)}
                        testId={`practitioner-service-rate-${s.id}`}
                        variant="dark"
                      />
                    ) : (
                      s.price_cents > 0 ? formatCardPrice(s.price_cents, s.currency) : t("card.free")
                    )}
                  </span>
                ))}
                {moreServices > 0 && <span className="text-[11px] text-white/40 self-center">+{moreServices}</span>}
              </div>
            )}

            {/* NO `payments_ready` GATE. The card used to hide "Book" behind the
                practitioner's own Stripe Connect onboarding, on the theory that the
                checkout would refuse the sale. It does not: Solunary is the merchant of
                record, the charge settles on the platform balance, and the practitioner's
                connected account only decides where their PAYOUT eventually goes. Hiding
                the link removed a bookable practice from the marketplace for a reason the
                visitor has no stake in. */}
            {/* TWO WAYS OUT, BOTH TO THE PRACTICE'S OWN ORIGIN: "Book" to its /reserver, and
                "Visit website" to its home page, where the full story lives. `mt-auto` pins
                the row to the card's foot so the buttons line up across a row of cards of
                different heights. */}
            <div className="mt-auto flex gap-2">
              <SiteLink
                siteUrl={p.site_url}
                tenantSlug={p.slug}
                path="/reserver"
                data-testid={`practitioner-book-${p.tenant_id}`}
                className="flex-1 inline-flex items-center justify-center px-4 h-10 rounded-full bg-sol-gold text-black text-sm font-medium hover:bg-sol-gold/90 transition"
              >
                {t("card.book")}
              </SiteLink>
              <SiteLink
                siteUrl={p.site_url}
                tenantSlug={p.slug}
                path="/"
                data-testid={REVIEWS.cardVisitSite(p.tenant_id)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-full border border-sol-gold/60 text-sol-gold text-sm font-medium hover:bg-sol-gold/10 transition"
              >
                {t("profile.visitSite")} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              </SiteLink>
            </div>
          </div>
        );
      })}
    </div>
  );
}
